import { useEffect, useState, useContext } from "react";
import { Spinner } from "react-bootstrap";
import {
  createNewAiChatroom,
  dateFirestoreToReact,
  dateReactToFirestore,
  enqueueAIChat,
  getChatroomsOfProject,
  getMessagesOfAiChatroom,
  writeAiChatUserMessage,
} from "../../helpers";
import "./ChatAi.css";
import CustomDropdown from "../CustomDropdown/CustomDropdown";
import config from "../../config";
import { UserInfoContext } from "../../contexts";
import Messages from "../reusables/MessagesContainer/Messages";
import { db } from "../../firebase.js";
import { collection, doc, onSnapshot } from "firebase/firestore";
import TypingIndicator from "../reusables/MessagesContainer/TypingIndicator.js";
import { useLocation } from "react-router-dom";

const dropdownOptions = Object.keys(config.PREMADE_AI_QUESTIONS).map((key) => ({
  value: key,
  label: config.PREMADE_AI_QUESTIONS[key],
}));

function sortMessages(messages) {
  return messages.sort((a, b) => {
    const timeA = a.createdAt.seconds * 1000 + a.createdAt.nanoseconds / 1e6;
    const timeB = b.createdAt.seconds * 1000 + b.createdAt.nanoseconds / 1e6;
    return timeB - timeA;
  });
}

const ChatAi = (props) => {
  const [selectedChat, setSelectedChat] = useState(null);
  const [newChatId, setNewChatId] = useState(null);

  return (
    <>
      <div>Hello {props.userInfo.firstName}</div>
      <div className="ai-chat-history-container hide-scrollbar">
        <OlderChats
          selectedProject={props.selectedProject}
          selectedChat={selectedChat}
          setSelectedChat={setSelectedChat}
          newChatId={newChatId}
        />
      </div>
      <div className="ai-chat-main-container">
        <ChatHeader
          selectedChat={selectedChat}
          setSelectedChat={setSelectedChat}
          setNewChatId={setNewChatId}
          selectedProject={props.selectedProject}
        />
        <ChatBody selectedChat={selectedChat} />
      </div>
    </>
  );
};

const ChatHeader = ({
  selectedChat,
  setSelectedChat,
  setNewChatId,
  selectedProject,
}) => {
  const userInfo = useContext(UserInfoContext);
  const [dropdownOpen, setOpenDropdown] = useState(false);
  const location = useLocation();

  async function handleNewChatroomClick(option) {
    const now = dateReactToFirestore(new Date());
    const chatroomData = {
      createdAt: now,
      updatedAt: now,
      projectId: selectedProject.value,
      title: option.label,
      typing: true,
      userId: userInfo.uid,
    };

    const chatroomId = await createNewAiChatroom(chatroomData);
    const AIChatData = {
      message: option.label,
      projectId: selectedProject.value,
      chatroomId: chatroomId,
      userId: userInfo.uid,
      idSite: selectedProject.matomoId,
      currency: selectedProject.currency,
      purchaseValue: selectedProject.purchaseValue,
      hasTracking: selectedProject.trackingStartDate ? true : false,
    };

    enqueueAIChat(AIChatData);
    await writeAiChatUserMessage(chatroomId, option.label, userInfo.uid, now);

    setNewChatId(chatroomId);
    setSelectedChat("dummy");
  }

  useEffect(() => {
    if (!selectedProject || !userInfo || !userInfo.uid || selectedChat) return;
    const queryParams = new URLSearchParams(location.search);
    const questionId = queryParams.get("qid");
    const question = config.PREMADE_AI_QUESTIONS[questionId];
    if (!question) return;

    handleNewChatroomClick({ label: question });
  }, []);

  return (
    <>
      <h2 className="fc-black3 fw-500 fs-24 mb-10">Luxifer AI</h2>
      <h3 className="fc-grey fw-500 fs-16 mb-30">
        Ask anything about your project to Luxifer AI.
      </h3>
      <hr></hr>
      <div className="fc-black fw-500 fs-16 mt-30">
        Select a question you want to ask
      </div>

      <div className="d-flex segment-funnel-dropdown-colours">
        <div className="w-100 mt-20">
          <CustomDropdown
            options={dropdownOptions}
            onChange={async (option) => {
              handleNewChatroomClick(option);
            }}
            value={selectedChat}
            placeholder="Select an option to ask a question"
            customControlClassNames="ai-chat-dropdown"
            unstyled
            focusSelection
            blackChevron
            menuOpenState={dropdownOpen}
            setMenuOpenState={(isOpen) => setOpenDropdown(isOpen)}
          />
        </div>
      </div>
    </>
  );
};

const OlderChats = ({
  selectedProject,
  selectedChat,
  setSelectedChat,
  newChatId,
}) => {
  const userInfo = useContext(UserInfoContext);
  const [chatRooms, setChatRooms] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!selectedProject) return;
    setLoading(true);
    setSelectedChat(null);

    getAiChatrooms(selectedProject.value);
  }, [selectedProject]);

  useEffect(() => {
    // Don't trigger loading
    if (!selectedProject || !newChatId) return;
    reloadAndSelectAiChatroom(newChatId);
  }, [newChatId]);

  async function reloadAndSelectAiChatroom(newChatId) {
    const rooms = await getAiChatrooms(selectedProject.value);
    if (!rooms || rooms.length == 0) return;
    const room = rooms.filter((room) => room.id === newChatId)[0];

    setSelectedChat({ value: room.id, label: room.title, ...room });
  }

  async function getAiChatrooms(pid) {
    const aiChatrooms = await getChatroomsOfProject(pid);

    const userChatrooms = aiChatrooms.filter(
      (chatroom) => chatroom.userId === userInfo.uid,
    );

    const chatroomMap = new Map();

    userChatrooms.forEach((chatroom) => {
      const date = dateFirestoreToReact(chatroom.createdAt);
      const year = date.getFullYear();
      const month = date.getMonth();
      const key = `${year}-${String(month + 1).padStart(2, "0")}`;
      const label = `${date.toLocaleString("default", {
        month: "long",
      })} ${year}`;

      if (!chatroomMap.has(key)) {
        chatroomMap.set(key, { label, chatrooms: [] });
      }

      chatroomMap.get(key).chatrooms.push({
        ...chatroom,
        label: chatroom.title,
        value: chatroom.id,
      });
    });

    for (const entry of chatroomMap.values()) {
      entry.chatrooms.sort((a, b) => b.createdAt - a.createdAt);
    }

    const orderedChatrooms = Array.from(chatroomMap.entries())
      .sort(([keyA], [keyB]) => keyB.localeCompare(keyA))
      .map(([, group]) => group);

    setChatRooms(orderedChatrooms);
    setLoading(false);
    return userChatrooms;
  }

  return (
    <div>
      {loading ? (
        <div className="mt-100 hc">
          <Spinner style={{ height: "75px", width: "75px" }} />
        </div>
      ) : !chatRooms ? (
        <h3 className="mt-100 hc">Something went wrong</h3>
      ) : Object.keys(chatRooms).length === 0 ? (
        <>
          <h3 className="mt-100 hc">No previous conversations.</h3>
          <div className="mt-16 hc fs-16">
            Select from the dropdown to chat.
          </div>
        </>
      ) : (
        <>
          {chatRooms.map(({ label, chatrooms }) => (
            <div key={label} className="ml-100 mr-100 mt-50">
              <div className="fc-grey fw-500 fs-14">{label}</div>
              <ul>
                {chatrooms.map((chatroom) => (
                  <li
                    onClick={() => {
                      setSelectedChat(chatroom);
                    }}
                    className="mt-8"
                    key={chatroom.id}
                  >
                    <span
                      className={`${
                        selectedChat?.id === chatroom.id
                          ? "fc-blue"
                          : "fc-black"
                      } limit-2-lines-li underline-4 fs-16 fw-700`}
                    >
                      {chatroom.title}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </>
      )}
    </div>
  );
};

const ChatBody = ({ selectedChat }) => {
  const userInfo = useContext(UserInfoContext);
  const [messages, setMessages] = useState(null);
  const [loading, setLoading] = useState(true);

  async function getMessages() {
    const messages = await getMessagesOfAiChatroom(selectedChat.id);
    const orderedMessages = sortMessages(messages);

    setMessages(orderedMessages);
    setLoading(false);
  }

  useEffect(() => {
    setLoading(true);
    if (selectedChat && selectedChat.value && selectedChat.id) {
      getMessages(selectedChat.id);
    }

    if (!selectedChat) {
      setMessages(null);
      setLoading(false);
    }
  }, [selectedChat]);

  useEffect(() => {
    if (!selectedChat?.id) return;

    const messagesRef = collection(
      doc(db, "channelsAiChat", selectedChat.id),
      "messages",
    );
    const unsubscribe = onSnapshot(messagesRef, (snapshot) => {
      const updatedMessages = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      const orderedMessages = sortMessages(updatedMessages);
      setMessages(orderedMessages);
    });

    return () => {
      unsubscribe();
    };
  }, [selectedChat]);

  return (
    <div className="ai-chat-body-container">
      {loading ? (
        <div className="mt-100 hc">
          <Spinner style={{ height: "75px", width: "75px" }} />
        </div>
      ) : !messages ? (
        <></>
      ) : messages.length === 0 ? (
        <h3 className="mt-100 hc">No messages found.</h3>
      ) : (
        <div className="ai-chat-messages-wrapper">
          <Messages
            loading={false}
            channelMessages={messages}
            userInfo={userInfo}
            showDate={false}
            messageRecipientClassname="ai-chat-recipient"
            messageAuthorClassname="ai-chat-author"
            overflowY="hidden"
            props={{
              openedChannel: { icon: config.LUXIFER_AI_PFP },
              chatPartnerStatus: "online",
            }}
          />
          <div
            style={{
              position: "absolute",
              bottom: "0px",
              pointerEvents: "none",
            }}
          >
            <TypingIndicator
              userInfo={userInfo}
              userIsTyping={false}
              otherIsTyping={messages.length < 2}
              props={{
                openedChannel: {
                  icon: config.LUXIFER_AI_PFP,
                  title: "Luxifer AI",
                },
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatAi;
